import { Icon } from '@iconify/react';
import React, { useEffect } from 'react';
import '../../../../stylesheets/gallery-lightbox.scss';
import { GalleryImage } from './Gallery';
import useTranslate from '../../../../../../customHooks/useTranslate';

interface GalleryLightboxProps<T extends GalleryImage> {
    items: T[];
    activeIndex: number | null;
    onClose: () => void;
    onNavigate: (index: number) => void;
}

const GalleryLightbox = <T extends GalleryImage>({
    items,
    activeIndex,
    onClose,
    onNavigate,
}: GalleryLightboxProps<T>) => {
    const translate = useTranslate();
    const isOpen = activeIndex !== null;

    const goToPrevious = () => {
        if (activeIndex === null) {
            return;
        }
        onNavigate((activeIndex - 1 + items.length) % items.length);
    };

    const goToNext = () => {
        if (activeIndex === null) {
            return;
        }
        onNavigate((activeIndex + 1) % items.length);
    };

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            } else if (event.key === 'ArrowLeft') {
                goToPrevious();
            } else if (event.key === 'ArrowRight') {
                goToNext();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, activeIndex, items.length]);

    if (activeIndex === null) {
        return null;
    }

    const item = items[activeIndex];
    const { alt, className, ...rest } = item;
    const showNav = items.length > 1;

    return (
        <div
            className='gallery-lightbox'
            role='dialog'
            aria-modal='true'
            aria-label={alt}
        >
            {/* Sibling (not ancestor) of the buttons/content below, so their clicks never bubble here. */}
            <button
                type='button'
                className='gallery-lightbox__backdrop'
                onClick={onClose}
                aria-label={translate('close')}
            />

            <button
                type='button'
                className='gallery-lightbox__close'
                onClick={onClose}
                aria-label={translate('close')}
            >
                <Icon icon='mdi:close' aria-hidden='true' />
            </button>

            {showNav && (
                <button
                    type='button'
                    className='gallery-lightbox__nav gallery-lightbox__nav--prev'
                    onClick={goToPrevious}
                    aria-label={translate('previous_image')}
                >
                    <Icon icon='mdi:chevron-left' aria-hidden='true' />
                </button>
            )}

            <div className='gallery-lightbox__content'>
                <img
                    {...rest}
                    alt={alt}
                    sizes='90vw'
                    className={`gallery-lightbox__image${
                        className ? ` ${className}` : ''
                    }`}
                />
            </div>

            {showNav && (
                <button
                    type='button'
                    className='gallery-lightbox__nav gallery-lightbox__nav--next'
                    onClick={goToNext}
                    aria-label={translate('next_image')}
                >
                    <Icon icon='mdi:chevron-right' aria-hidden='true' />
                </button>
            )}
        </div>
    );
};

export default GalleryLightbox;
